/**
 * Roster Thumbnail Row Component
 *
 * Compact horizontal row of creature thumbnails shown in peek mode.
 * Shows 3-4 creature sprites with a "+N more" indicator and deployment counter.
 *
 * Features:
 * - Tap thumbnail to select creature (tap-to-select flow)
 * - Shows deployment count badge
 * - "See all" button to expand sheet
 */

import React from 'react';
import type { SerializedCreatureDocument } from '@shared/types/creature-storage';
import './RosterThumbnailRow.css';

export interface RosterThumbnailRowProps {
  /** Available creatures */
  creatures: SerializedCreatureDocument[];
  /** Set of deployed creature IDs */
  deployedCreatureIds: Set<string>;
  /** Number of creatures deployed */
  deployedCount: number;
  /** Maximum deployment allowed */
  maxDeployment: number;
  /** Called when creature is selected */
  onSelect?: (creature: SerializedCreatureDocument) => void;
  /** Currently selected creature ID */
  selectedCreatureId?: string | null;
  /** Called to expand the sheet */
  onExpand: () => void;
  /** Loading state */
  loading?: boolean;
}

export const RosterThumbnailRow: React.FC<RosterThumbnailRowProps> = ({
  creatures,
  deployedCreatureIds,
  deployedCount,
  maxDeployment,
  onSelect,
  selectedCreatureId,
  onExpand,
  loading,
}) => {
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  // Filter to non-deployed creatures first, then fill with deployed
  const availableCreatures = creatures.filter(c => !deployedCreatureIds.has(c.id));
  const deployedCreatures = creatures.filter(c => deployedCreatureIds.has(c.id));

  // Show all creatures - available first, then deployed
  const visibleCreatures = [
    ...availableCreatures,
    ...deployedCreatures,
  ];
  const isLimitReached = deployedCount >= maxDeployment;

  // Check scroll position to update indicators
  const checkScroll = React.useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  // Update scroll indicators on mount and when creatures change
  React.useEffect(() => {
    checkScroll();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      return () => container.removeEventListener('scroll', checkScroll);
    }
  }, [creatures, checkScroll]);

  // Scroll programmatically
  const scrollBy = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = direction === 'left' ? -200 : 200;
    container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  };

  const handleThumbnailClick = (creature: SerializedCreatureDocument, e: React.MouseEvent) => {
    e.stopPropagation();

    const isDeployed = deployedCreatureIds.has(creature.id);
    const isDisabled = isLimitReached && !isDeployed;

    if (isDisabled) return;

    onSelect?.(creature);
  };

  if (loading) {
    return (
      <div className="thumbnail-row">
        <div className="thumbnail-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="thumbnail-row">
      {/* Deployment counter */}
      <div className={`thumbnail-counter ${isLimitReached ? 'limit-reached' : ''}`}>
        {deployedCount}/{maxDeployment}
      </div>

      {/* Scroll left button */}
      {canScrollLeft && (
        <button
          className="thumbnail-scroll-btn thumbnail-scroll-left"
          onClick={() => scrollBy('left')}
          aria-label="Scroll left"
        >
          ‹
        </button>
      )}

      {/* Creature thumbnails */}
      <div className="thumbnail-list" ref={scrollContainerRef}>
        {visibleCreatures.map(creature => {
          const isDeployed = deployedCreatureIds.has(creature.id);
          const isDisabled = isLimitReached && !isDeployed;
          const isSelected = selectedCreatureId === creature.id;

          return (
            <button
              key={creature.id}
              className={`thumbnail-item ${isDeployed ? 'deployed' : ''} ${isDisabled ? 'disabled' : ''} ${isSelected ? 'selected' : ''}`}
              onClick={(e) => handleThumbnailClick(creature, e)}
              disabled={isDisabled}
              title={creature.name}
              aria-label={`Select ${creature.name}`}
            >
              <img
                src={creature.sprites.menuSprite}
                alt={creature.name}
                className="thumbnail-sprite"
                draggable={false}
              />
              {isDeployed && <div className="deployed-badge" />}
            </button>
          );
        })}

      </div>

      {/* Scroll right button */}
      {canScrollRight && (
        <button
          className="thumbnail-scroll-btn thumbnail-scroll-right"
          onClick={() => scrollBy('right')}
          aria-label="Scroll right"
        >
          ›
        </button>
      )}

      {/* Expand button */}
      <button
        className="expand-button"
        onClick={onExpand}
        aria-label="Expand creature roster"
      >
        <span className="expand-icon">&#9650;</span>
      </button>
    </div>
  );
};

export default RosterThumbnailRow;
